/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import React, { useMemo, useState, useCallback } from 'react';
import { BaseFilter } from '../BaseFilter';
import { Popup } from '../../Popup';
import { Checkbox } from '../../inputs/Checkbox';
import { reconcileTreeSelection, selectedLeafCount } from './reconcileTreeSelection';
import './TreeMultiSelectFilterStyles.scss';
function filterTree(options, query) {
    if (query.trim() === '')
        return options;
    const normalizedQuery = query.toLowerCase();
    return options
        .map((option) => {
        var _a;
        const parentMatch = option.label.toLowerCase().includes(normalizedQuery);
        const children = (_a = option.items) !== null && _a !== void 0 ? _a : [];
        const matchingChildren = children.filter((child) => child.label.toLowerCase().includes(normalizedQuery));
        if (parentMatch)
            return option;
        if (matchingChildren.length > 0)
            return Object.assign(Object.assign({}, option), { items: matchingChildren });
        return null;
    })
        .filter((option) => option != null);
}
function flattenOptions(options) {
    var _a;
    const flattened = [];
    const stack = [...options];
    while (stack.length > 0) {
        const current = stack.shift();
        if (!current)
            continue;
        flattened.push(current);
        if ((_a = current.items) === null || _a === void 0 ? void 0 : _a.length) {
            stack.unshift(...current.items);
        }
    }
    return flattened;
}
export function TreeMultiSelectFilter({ label, value, onChange, options, placeholder = 'Search', disabled = false, showReset = true, nonSelectableValues = [] }) {
    const [search, setSearch] = useState('');
    const [expandedValues, setExpandedValues] = useState([]);
    const nonSelectable = useMemo(() => new Set(nonSelectableValues), [nonSelectableValues]);
    const flatOptions = useMemo(() => flattenOptions(options), [options]);
    const byValue = useMemo(() => new Map(flatOptions.map((option) => [option.value, option])), [flatOptions]);
    const filteredOptions = useMemo(() => filterTree(options, search), [options, search]);
    const leafCount = useMemo(() => selectedLeafCount(options, value), [options, value]);
    const selectedDisplay = useMemo(() => value.map((selectedValue) => { var _a, _b; return (_b = (_a = byValue.get(selectedValue)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : String(selectedValue); }).join(', '), [byValue, value]);
    const handleToggleOption = useCallback((option) => {
        onChange(reconcileTreeSelection(options, value, option));
    }, [onChange, options, value]);
    const handleReset = useCallback(() => onChange([]), [onChange]);
    const toggleExpanded = useCallback((nodeValue) => {
        setExpandedValues((current) => current.includes(nodeValue)
            ? current.filter((expandedValue) => expandedValue !== nodeValue)
            : [...current, nodeValue]);
    }, []);
    return (React.createElement(BaseFilter, { label: label, isActive: value.length > 0, selectedDisplay: selectedDisplay, count: leafCount > 0 ? leafCount : undefined, disabled: disabled, onReset: showReset && value.length > 0 ? handleReset : undefined }, () => (React.createElement("div", { className: "qmb-ui-tree-multi-select-filter" },
        React.createElement(Popup.SearchInput, { value: search, placeholder: placeholder, onChange: setSearch }),
        React.createElement(Popup.Section, { fillHeight: true },
            React.createElement("ul", { className: "qmb-ui-tree-multi-select-filter__list", role: "presentation" },
                filteredOptions.length === 0 && React.createElement("li", { className: "qmb-ui-tree-multi-select-filter__empty" }, "No results"),
                filteredOptions.map((option) => {
                    var _a, _b;
                    const isExpanded = expandedValues.includes(option.value);
                    const hasChildren = Boolean((_a = option.items) === null || _a === void 0 ? void 0 : _a.length);
                    const isSelectable = !nonSelectable.has(option.value);
                    // A non-selectable parent can never be selected, so its children
                    // are never implicitly selected by it either.
                    const isParentSelected = isSelectable && value.includes(option.value);
                    return (React.createElement("li", { key: String(option.value), className: "qmb-ui-tree-multi-select-filter__row" },
                        React.createElement("div", { className: "qmb-ui-tree-multi-select-filter__item" },
                            isSelectable ? (React.createElement(Checkbox, { id: `tree-multi-parent-${String(option.value)}`, checked: isParentSelected, onChange: () => handleToggleOption(option), label: option.label })) : (React.createElement("span", { className: "qmb-ui-tree-multi-select-filter__group-label" }, option.label)),
                            hasChildren && (React.createElement("button", { type: "button", className: "qmb-ui-tree-multi-select-filter__expand", "aria-label": `Toggle ${option.label}`, "aria-expanded": isExpanded, "aria-controls": `tree-multi-children-${String(option.value)}`, onClick: () => toggleExpanded(option.value) },
                                React.createElement("i", { className: `fa-light ${isExpanded ? 'fa-angle-up' : 'fa-angle-down'}`, "aria-hidden": "true" })))),
                        hasChildren && isExpanded && (React.createElement("ul", { id: `tree-multi-children-${String(option.value)}`, className: "qmb-ui-tree-multi-select-filter__children", role: "presentation" }, (_b = option.items) === null || _b === void 0 ? void 0 : _b.map((child) => (React.createElement("li", { key: String(child.value) },
                            React.createElement(Checkbox, { id: `tree-multi-child-${String(child.value)}`, checked: value.includes(child.value) || isParentSelected, onChange: () => handleToggleOption(child), label: child.label }))))))));
                })))))));
}
